import { useLatestCallback } from '@beatgig/helpers/use-latest-callback'
import { useEffect, useState } from 'react'

export function useDebounce<T>(
  value: T,
  delay = 200,
  callback?: (v: T) => void
) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)

  const onChange = useLatestCallback((value: T) => callback?.(value))

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )

  useEffect(
    function callbackOnChange() {
      onChange(debouncedValue)
    },
    [debouncedValue, onChange]
  )

  return debouncedValue
}

export default useDebounce
